import { useEffect, useCallback, useRef } from 'react';
import { SoundEffect, playSound, preloadSounds, createDebouncedSound } from '../lib/soundEffects';

/**
 * Hook for accessing sound effects in React components
 * @returns Various sound effect functions and utilities
 */
export const useSoundEffects = () => {
    // Add a ref to track click velocity for dynamic effects
    const clickInfo = useRef({ lastClickTime: 0, count: 0 });

    useEffect(() => {
        // Preload sounds when component mounts
        preloadSounds();
    }, []);

    // Direct sound functions (not for React events)
    const playSoundDirect = useCallback((effect: SoundEffect, volume?: number) => playSound(effect, volume), []);

    // React event handlers with enhanced responsiveness
    const hoverSound = useCallback((e?: React.MouseEvent) => {
        // Get the actual target element to check for responsiveness
        const target = e?.currentTarget as HTMLElement;

        // Immediately play the sound for instant feedback
        playSound(SoundEffect.HOVER);

        // Add temporary class for tracking hover state
        if (target) {
            target.classList.add('sound-hover-active');
            // Remove the class after the hover effect completes
            setTimeout(() => target.classList.remove('sound-hover-active'), 300);
        }
    }, []);

    const clickSound = useCallback((e?: React.MouseEvent) => {
        // Detect rapid clicks to adjust sound
        const now = Date.now();
        const timeSinceLastClick = now - clickInfo.current.lastClickTime;

        // Track click pattern
        if (timeSinceLastClick < 500) {
            clickInfo.current.count++;
        } else {
            clickInfo.current.count = 0;
        }
        clickInfo.current.lastClickTime = now;

        // Get click target
        const target = e?.currentTarget as HTMLElement;

        // Play sound with volume based on click pattern or element type
        let volume = 0.35; // Default volume

        if (clickInfo.current.count > 2) {
            // Louder for rapid clicks
            volume = 0.45;
        } else if (target) {
            // Adjust volume based on element type
            if (target.tagName === 'BUTTON' && target.classList.contains('primary')) {
                volume = 0.4; // Slightly louder for primary buttons
            } else if (target.hasAttribute('data-important')) {
                volume = 0.4; // Louder for important elements
            }
        }

        // Play sound immediately for best responsiveness
        playSound(SoundEffect.CLICK, volume);

        // Add temporary animation class for visual feedback
        if (target) {
            target.classList.add('sound-click-active');
            setTimeout(() => target.classList.remove('sound-click-active'), 300);
        }
    }, []);

    const transitionSound = useCallback((volume?: number) => playSound(SoundEffect.TRANSITION, volume), []);
    const successSound = useCallback(() => playSound(SoundEffect.SUCCESS), []);
    const errorSound = useCallback(() => playSound(SoundEffect.ERROR), []);
    const typingSound = useCallback((volume?: number) => playSound(SoundEffect.TYPING, volume), []);

    // For use with onChange events in inputs - more responsive typing sound
    const handleTypingSound = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        // Play typing sound more frequently but with varying volume for natural feel
        const charCount = value.length;
        if (charCount % 2 === 0) {
            // Vary volume slightly based on typing pattern for more natural sound
            const variance = (charCount % 6) / 100;
            playSound(SoundEffect.TYPING, 0.1 + variance);
        }
    }, []);

    // Enhanced scroll sound for real-time scrolling feedback
    const scrollSound = useCallback((velocity?: number) => {
        // Volume based on scroll velocity
        const baseVolume = 0.2;
        const volumeAdjust = velocity ? Math.min(Math.abs(velocity) / 100, 0.2) : 0;
        playSound(SoundEffect.SCROLL, baseVolume + volumeAdjust);
    }, []);

    // Debounced versions with shorter delays for more responsiveness
    const debouncedScrollSound = useCallback(createDebouncedSound(SoundEffect.SCROLL, 100), []);
    const debouncedHoverSound = useCallback((e?: React.MouseEvent) => {
        // Even shorter debounce time for hover
        const debouncedFn = createDebouncedSound(SoundEffect.HOVER, 20);
        debouncedFn();
    }, []);

    // Enhanced utility to attach sound to any element's event with action detection
    const attachSoundToEvent = useCallback((
        element: HTMLElement | null,
        eventName: string,
        effect: SoundEffect,
        options?: {
            volume?: number,
            debounce?: boolean,
            debounceTime?: number,
            detectAction?: boolean
        }
    ) => {
        if (!element) return () => { };

        const {
            volume,
            debounce = false,
            debounceTime = 100,
            detectAction = true
        } = options || {};

        // Enhanced sound handler with action detection
        const soundHandler = (event: Event) => {
            let dynamicVolume = volume;

            // Detect action intensity if enabled
            if (detectAction) {
                if (eventName === 'click') {
                    // Could detect click intensity by checking event.detail in MouseEvent
                    const mouseEvent = event as MouseEvent;
                    if (mouseEvent.shiftKey) {
                        // Increase volume slightly for modified clicks
                        dynamicVolume = volume ? volume * 1.2 : 0.4;
                    }
                } else if (eventName === 'scroll') {
                    // Could adjust based on scroll velocity
                    // This would require tracking previous scroll position
                }
            }

            if (debounce) {
                const debouncedFn = createDebouncedSound(effect, debounceTime);
                debouncedFn(dynamicVolume);
            } else {
                // Immediate playback for most events, minimal delay for hover
                if (eventName === 'mouseenter' && effect === SoundEffect.HOVER) {
                    playSound(effect, dynamicVolume);
                } else {
                    playSound(effect, dynamicVolume);
                }
            }
        };

        // Add event listener with proper type
        element.addEventListener(eventName, soundHandler);

        // Return cleanup function
        return () => {
            element.removeEventListener(eventName, soundHandler);
        };
    }, []);

    return {
        playSound: playSoundDirect,
        hoverSound,
        clickSound,
        transitionSound,
        successSound,
        errorSound,
        typingSound,
        handleTypingSound,
        scrollSound,
        debouncedScrollSound,
        debouncedHoverSound,
        attachSoundToEvent,
    };
}; 